/* eslint-disable max-lines */
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import {
    AutocompleteElement,
    CheckboxElement,
    FormContainer,
    PasswordElement,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Stack, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';

import {
    EditButtons,
    ImageField,
    MaskedTextFieldElement,
    TripleColumnLayout,
} from '@barboxx/admin-components';
import { useAuth } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { User } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import {
    BUSINESS_ROLES,
    ImageKitCustomMetadataKeys,
    ImageKitFolders,
    ImageKitNamedTransforms,
    ImageKitTypes,
    ImageKitUserTypes,
    INTERNAL_ROLES,
    USER_TYPES,
    WEB_AVATAR_SIZE,
} from '@barboxx/types';
import { enumToFormOptions, enumToList, getInitials } from '@barboxx/utils';

import { REACT_APP_ENV } from '../../../../../environments/environment';

interface Props<T extends FieldValues> {
    formContext: UseFormReturn<T>;
    onSubmit: (user: T) => Promise<void>;
    editableOnLoad?: boolean;
}

export function UserForm<T extends FieldValues = User.UpdateRequest>({
    formContext,
    onSubmit,
    editableOnLoad = false,
}: Props<T>) {
    // Generated by Copilot
    const [editable, editableActions] = useBool(editableOnLoad);
    const { auth } = useAuth();
    const { setValue, formState, getValues } = formContext;
    const values = getValues() as unknown as User;
    const initials = getInitials(values);

    const handleCancel = () => {
        formContext.reset(undefined);

        if (!editableOnLoad) {
            editableActions.off();
        }
    };

    const businessesQuery = useQuery({
        queryFn: () => Api.Businesses.getBusinesses({}),
        queryKey: ['businesses'],
    });

    React.useEffect(() => {
        // All of this is to set the default business if its included in the default values as businessIds from the url
        if (businessesQuery.isFetched && !formState.isDirty) {
            const businessIdsDefault =
                (formState.defaultValues?.['businessIds'] as number[]) ?? [];

            if (businessIdsDefault.length) {
                const businessesPresets =
                    businessesQuery.data?.data?.results.filter(
                        b => !!businessIdsDefault.includes(b.id)
                    );

                if (businessesPresets?.length) {
                    setValue(
                        'businesses' as Path<T>,
                        businessesPresets as PathValue<T, Path<T>>,
                        { shouldDirty: false, shouldTouch: false }
                    );
                }
            }
        }
    }, [
        businessesQuery.data?.data?.results,
        businessesQuery.isFetched,
        formState.isDirty,
        formState.defaultValues,
        setValue,
    ]);

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Stack gap={2}>
                <TripleColumnLayout
                    LeftContent={
                        <Stack gap={2}>
                            <TextFieldElement
                                required
                                name="firstName"
                                label="First Name"
                                slotProps={{
                                    input: { readOnly: !editable },
                                }}
                            />
                            <TextFieldElement
                                required
                                name="lastName"
                                label="Last Name"
                                slotProps={{
                                    input: { readOnly: !editable },
                                }}
                            />
                            <TextFieldElement
                                required
                                name="email"
                                label="Email"
                                slotProps={{
                                    input: { readOnly: !editable },
                                }}
                            />
                            <MaskedTextFieldElement
                                name="phoneNumber"
                                label="Phone Number"
                                type="tel"
                                slotProps={{
                                    input: { readOnly: !editable },
                                }}
                                inputProps={{
                                    mask: '(#00) 000-0000',
                                    definitions: {
                                        '#': /[1-9]/,
                                    },
                                }}
                            />
                        </Stack>
                    }
                    CenterContent={
                        <Stack alignItems="center" gap={2} flex={1}>
                            <ImageField
                                label="Avatar"
                                name="avatar"
                                readOnly={!editable || !values.id}
                                imagekitUploadProps={{
                                    fileName: `${values.id}`,
                                    folder: ImageKitFolders.UserAvatar,
                                    env: REACT_APP_ENV,
                                    customMetadata: {
                                        [ImageKitCustomMetadataKeys.Id]: `${values.id}`,
                                        [ImageKitCustomMetadataKeys.UserType]:
                                            ImageKitUserTypes.User,
                                        [ImageKitCustomMetadataKeys.Type]:
                                            ImageKitTypes.Avatar,
                                        [ImageKitCustomMetadataKeys.Env]:
                                            REACT_APP_ENV,
                                    },
                                    tags: [
                                        `${values.id}`,
                                        ImageKitTypes.Avatar,
                                        ImageKitUserTypes.User,
                                        REACT_APP_ENV,
                                    ],
                                    transformation: {
                                        post: [
                                            {
                                                type: 'transformation',
                                                value: ImageKitNamedTransforms
                                                    .Web.square,
                                            },
                                        ],
                                    },
                                }}
                                imagekitProps={{
                                    alt: 'User photo',
                                    height: WEB_AVATAR_SIZE,
                                    width: WEB_AVATAR_SIZE,
                                    transformation: [
                                        {
                                            raw: ImageKitNamedTransforms.Web
                                                .square,
                                        },
                                        {
                                            raw: ImageKitNamedTransforms.Common
                                                .avatar,
                                        },
                                    ],
                                    loading: 'lazy',
                                    lqip: {
                                        raw: ImageKitNamedTransforms.Web
                                            .squareLq,
                                    },
                                }}
                                EmptyImageComponent={() => (
                                    <Avatar
                                        sx={{
                                            width: WEB_AVATAR_SIZE,
                                            height: WEB_AVATAR_SIZE,
                                        }}
                                    >
                                        {initials && (
                                            <Typography
                                                fontSize="8rem"
                                                variant="h6"
                                            >
                                                {initials}
                                            </Typography>
                                        )}
                                    </Avatar>
                                )}
                            />
                        </Stack>
                    }
                    RightContent={
                        <Stack gap={2}>
                            {values.id ? (
                                <Stack
                                    direction="row"
                                    gap={2}
                                    alignItems="center"
                                >
                                    <TextFieldElement
                                        name="id"
                                        label="User ID"
                                        slotProps={{
                                            input: { readOnly: true },
                                        }}
                                    />
                                    <CheckboxElement
                                        name="active"
                                        label="Active"
                                        disabled={
                                            !editable ||
                                            auth.user?.id === values.id
                                        }
                                    />
                                </Stack>
                            ) : (
                                <PasswordElement
                                    name="password"
                                    label="Password"
                                    required
                                    slotProps={{
                                        input: { readOnly: !editable },
                                    }}
                                />
                            )}

                            <SelectElement
                                name="userType"
                                label="User Type"
                                fullWidth
                                slotProps={{
                                    input: {
                                        readOnly:
                                            !editable ||
                                            auth?.user?.userType !== 'Internal',
                                    },
                                }}
                                options={enumToFormOptions(USER_TYPES)}
                                onChange={() => {
                                    formContext.setValue(
                                        'roles' as Path<T>,
                                        [] as PathValue<T, Path<T>>
                                    );
                                    formContext.setValue(
                                        'businesses' as Path<T>,
                                        [] as PathValue<T, Path<T>>
                                    );
                                }}
                            />

                            <AutocompleteElement
                                name="roles"
                                label="Roles"
                                multiple
                                options={enumToList(
                                    values.userType === 'Internal'
                                        ? INTERNAL_ROLES
                                        : BUSINESS_ROLES
                                )}
                                autocompleteProps={{
                                    fullWidth: true,
                                    readOnly: !editable,
                                    multiple: true,
                                }}
                            />
                        </Stack>
                    }
                />

                {values.userType === 'Business' && (
                    <AutocompleteElement
                        name="businesses"
                        label="Business"
                        required
                        loading={businessesQuery.isLoading}
                        multiple
                        options={businessesQuery.data?.data?.results ?? []}
                        autocompleteProps={{
                            fullWidth: true,
                            readOnly: !editable,
                            isOptionEqualToValue(option, value) {
                                return option.id === value.id;
                            },
                            getOptionLabel(option) {
                                if (typeof option === 'string') {
                                    return option;
                                }
                                return option?.place?.name || '';
                            },
                        }}
                    />
                )}
                <Can I="update" a="User">
                    <EditButtons
                        formContext={formContext}
                        handleCancel={handleCancel}
                        editable={editable}
                        editableActions={editableActions}
                    />
                </Can>
            </Stack>
        </FormContainer>
    );
}
/* eslint-enable max-lines */
